import React, { useState } from "react";
import EmailInput from "./components/EmailInput";
import Modal from "../../../../components/Modal/Modal";
import Button from "../../../../components/Button/Button";
import DownloadTrayIcon from "../../../../assets/svg/download-tray-icon";
import * as Yup from "yup";
import VerbamailIcon from "../../../../assets/svg/verbamail-icon";
import CloseIcon from "../../../../assets/svg/close-icon";
import { baseUrl } from "../../../../redux/services/baseApi";
import { customToast } from "../../../../utils/custom-toast/custom-toast";
import SendIcon from "../../../../assets/svg/send-icon";
import { getClientDomain } from "../../../../utils/functions/client-code";
import { trackEvents } from "../../../../utils/amplitude/amplitude";

const ChatExportModal = ({
  open,
  onOpenChange,
  leadId,
  module = "communication",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId?: string;
  module?: "communication" | "campaign" | "crm";
}) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState<boolean | string>(false);

  const validateEmails = async (emailList: string[]) => {
    const emailSchema = Yup.string().email("Invalid email format");
    const validEmails: string[] = [];
    const invalidEmails: string[] = [];

    for (const email of emailList) {
      try {
        await emailSchema.validate(email);
        validEmails.push(email);
      } catch {
        invalidEmails.push(email);
      }
    }

    if (invalidEmails.length > 0) {
      setEmailError(`Invalid email(s): ${invalidEmails.join(", ")}`);
      return false;
    }

    setEmailError("");
    return true;
  };

  const handleEmailChange = (validEmails: string[]) => {
    setEmails(validEmails);
    setEmailError("");
  };

  const handleExportChat = async (emailsToSend?: string) => {
    if (emailsToSend && emailsToSend.length === 0) {
      setEmailError("Please enter at least one valid email address");
      return;
    }

    if (emailsToSend) {
      const isValid = await validateEmails([emailsToSend]);
      if (!isValid) return;
    }

    setIsLoading(emailsToSend ? "email" : "export");
    const clientCode = getClientDomain();
    try {
      const response = await fetch(
        `${baseUrl}/dashboard/lead/chat-transcript-email-download/${leadId}`,
        {
          method: "POST",
          body: emailsToSend
            ? JSON.stringify({ email_id: emailsToSend })
            : undefined,
          credentials: "include",
          headers: {
            "X-Client-Code": clientCode || "",
          },
        }
      );

      if (response.status === 400 || !response.ok) {
        const responseData = await response.json().catch(() => ({}));
        const errorMessage = responseData?.detail || "Something went wrong";
        trackEvents("chat_export_failed", {
          lead_id: leadId || "",
          module,
          export_type: emailsToSend ? "email" : "download",
          error: errorMessage,
          status_code: response.status,
          org_id: clientCode || "",
          timestamp: new Date().toISOString(),
        });
        customToast(errorMessage, "error");
        return;
      }

      if (emailsToSend) {
        const responseData = await response.json().catch(() => ({}));
        trackEvents("chat_exported", {
          lead_id: leadId || "",
          module,
          export_type: "email",
          recipient_count: emailsToSend.split(",").length,
          org_id: clientCode || "",
          timestamp: new Date().toISOString(),
        });
        customToast(
          responseData?.message || "Chat transcript sent successfully",
          "success"
        );
        onOpenChange(false);
        setEmails([]);
        return;
      }

      const blob = await response.blob();
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.href = url;
      link.download =
        response?.headers?.get("Content-Disposition")?.split("=")[1] ??
        `chat_transcript_${leadId}.xlsx`;
      document.body.appendChild(link);
      link.click();

      trackEvents("chat_exported", {
        lead_id: leadId || "",
        module,
        export_type: "download",
        org_id: clientCode || "",
        timestamp: new Date().toISOString(),
      });

      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      onOpenChange(false);
    } catch (error) {
      const errorMessage =
        (error as { data: { detail: string } })?.data?.detail ||
        "Something went wrong";
      trackEvents("chat_export_failed", {
        lead_id: leadId || "",
        module,
        export_type: emailsToSend ? "email" : "download",
        error: errorMessage,
        org_id: clientCode || "",
        timestamp: new Date().toISOString(),
      });
      customToast(errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setEmails([]);
      setEmailError("");
    }
    onOpenChange(newOpen);
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      description="chat export modal"
      contentClassName="bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-[8px] shadow-popper-blur-effect backdrop-blur-[10px]"
      showCloseButton={false}
    >
      <div className="flex flex-col gap-[16px] w-[400px]">
        <div className="flex justify-between">
          <div className="flex flex-col gap-[8px]">
            <div className="flex items-center gap-[6px] text-heading-text dark:text-heading-text-dk ">
              <DownloadTrayIcon className="w-[18px] h-[18px]" />
              <p className="font-semibold text-md">Export Chat</p>
            </div>
            <p className="text-verba-field-label dark:text-verba-field-label-dk text-sm font-normal">
              Chat will be exported and downloaded in xlsx format
            </p>
            <Button
              innerText="Export Chat"
              innerTextClass="text-md font-medium text-heading-text dark:text-heading-text-dk"
              buttonContainerClass="w-[110px] bg-transparent border border-verba-blue-dark-mode dark:border-verba-blue-dark-mode rounded-[8px] py-[4px]"
              onClick={() => handleExportChat()}
              isLoading={isLoading === "export"}
            />
          </div>
          <button
            className={`w-[15px] h-[15px] bg-tag-gray dark:bg-tag-gray-dk rounded-full flex flex-col items-center justify-center transition-transform duration-200`}
            aria-label="Close modal"
            onClick={() => onOpenChange(false)}
          >
            <CloseIcon className="w-[5.5px] h-[5.5px] text-verba-indigo dark:text-verba-indigo-dk" />
          </button>
        </div>

        <hr className="border-gray-100 dark:border-gray-100-dk" />

        <div className="flex flex-col gap-[8px]">
          <div className="flex items-center gap-[6px] text-heading-text dark:text-heading-text-dk ">
            <VerbamailIcon className="w-[18px] h-[18px]" />
            <p className="font-semibold text-md">Send on Email</p>
          </div>
          <p className="text-verba-field-label dark:text-verba-field-label-dk text-sm font-normal">
            Enter one or more email addresses separated by commas. The chat
            export will be sent to all listed recipients.
          </p>
          <EmailInput
            value={emails.join(", ")}
            onChange={handleEmailChange}
            placeholder="@ Enter email"
            error={emailError}
            errorClassName="text-red-500 text-sm mx-8"
            containerClassName="w-full"
            secondaryContainerClassName="w-full flex-1 bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-[8px] px-[12px] py-[8.5px]"
          />
          <Button
            innerText="Send"
            innerTextClass="text-md font-medium text-heading-text dark:text-heading-text-dk"
            Icon={SendIcon}
            iconClass="w-[14px] h-[14px] text-verba-blue-dark-mode dark:text-verba-blue-dark-mode"
            iconLeft={true}
            buttonContainerClass="w-[124px] bg-transparent border border-verba-blue-dark-mode dark:border-verba-blue-dark-mode rounded-[8px] py-[4px] mt-3"
            onClick={() => handleExportChat(emails.join(","))}
            disabled={emails.length === 0 || isLoading === "email"}
            isLoading={isLoading === "email"}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ChatExportModal;
